import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { BaseComponent } from '../../../../components/base/base.component';
import {
  CompanyLocationConfigEx,
  MasterPayrollObject,
  PayDateInfoEx,
  PayTypes,
  PayrollConfig,
  PayrollConfigSetupData,
  PayrollStartConfig,
  PayrollStartData,
  Schedule
} from '../../../../models/payroll';
import { AdminService } from '../../../../services/admin.service';
import { AuthService } from '../../../../services/auth.service';
import { BusinessDaysService } from '../../../../services/business-days.service';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../utils/datepicker-value-parser';
import { PayDateValidator } from '../../../../utils/paydate-validator';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Enum } from '../../../../models/base';
import { EnumService } from '../../../../services/enum.service';
import {
  AreDatesEqual,
  GetTimeInEST,
  fromNgbDate,
  minimumDate,
  toNgbDate
} from '../../../../utils/dateUtils';
import { OrderBy } from '../../../../utils/math';

@Component({
  selector: 'lib-payroll-start-schedule',
  templateUrl: './payroll-start-schedule.component.html',
  styleUrls: ['./payroll-start-schedule.component.css'],
  standalone: false
})
export class PayrollStartScheduleComponent extends BaseComponent implements OnInit {
  @Output() payDateChanged = new EventEmitter<Date>(true);

  @Input() location: CompanyLocationConfigEx;
  @Input() activePayroll: MasterPayrollObject;
  @Input() isCsr: boolean;
  @Input() startData: PayrollStartData;
  @Input() payrollConfig: PayrollConfig;
  @Input() payrollConfigSetupData: PayrollConfigSetupData;
  @Input() payrollStartConfig: PayrollStartConfig;
  @Input() locked: boolean;
  @Input() lockedByProcessDueDate: any;
  @Input() minimumAvailableDate: { month: number; year: number; day: number } | null = null;
  @Input() payType: PayTypes;

  public payrollLabels: Enum[];

  get PayTypeLabel(): string {
    if (this.payType == PayTypes.Regular) {
      return 'Regular';
    }

    return 'Off-Cycle';
  }

  isNaN = Number.isNaN;

  _selectedSchedules: Schedule[];

  @Input() set selectedSchedules(value: Schedule[]) {
    this._selectedSchedules = OrderBy(value, item => -1 * item.SeqNo);
  }
  get selectedSchedules(): Schedule[] {
    return this._selectedSchedules;
  }

  public holidays: any[];
  public selSchedule: PayDateInfoEx;
  public selCalendarId: number;
  public scheduleError: any;
  @Input() payrollScheduleForm: UntypedFormGroup;

  public get f() {
    return this.payrollScheduleForm?.controls;
  }

  public get IsValid() {
    return (
      Object.values(this.payrollScheduleForm.controls).every(c => c.valid) &&
      this.selectedSchedules.every(
        s => s.PeriodEnd != null && !this.isNaN(new Date(s.PeriodEnd).getTime())
      )
    );
  }

  constructor(
    private adminService: AdminService,
    private authService: AuthService,
    private businessDayService: BusinessDaysService,
    private formBuilder: FormBuilder,
    private element: ElementRef,
    private enumService: EnumService,
    private rootFormGroup: FormGroupDirective,
    @Inject('env') private env: any
  ) {
    super();
  }

  ngOnInit(): void {
    this.enumService
      .getRelay('PayrollLabels')
      .pipe(map(labels => (this.payrollLabels = labels)))
      .subscribe();

    this.payrollScheduleForm = this.rootFormGroup.control;
    this.onScheduleChange(true);

    this.f.PayDate.valueChanges.subscribe(() => {
      // We must update the form validity so that consumers of the payDateChanged event get the correct value
      this.f.PayDate.updateValueAndValidity({ emitEvent: false });
      this.payDateChanged.emit(parseDatePickerValue(this.f.PayDate.value));
    });
  }

  // #region public methods

  public onScheduleChange(onload: boolean = false) {
    let elements = this.element.nativeElement.querySelectorAll('.periodEndControl');
    elements.control;

    // There is seemingly no way around this setTimeout if we want to have this form in a child component...
    //
    // If we set the initial value onInit, we get the angular error: Expression has changed after it was checked
    // which is referring to the disabled parameter of the submit button in the parent component
    //
    // This error only appears in dev mode (and unit tests), and the disabled parameter seems to update itself
    // correctly even with the error displaying.  By using set timeout, we delay setting the paydate field by
    // one 'frame' which shouldn't impact the UX in any perceptible way...  Despite being gross, this seems to
    // be the least gross way to mitigate the problem that I have found in the online Angular community.
    //
    // If you decide to take this out and just initialize the control value directly, make sure that all
    // unit tests in payroll-start.component.spec.ts pass.
    //
    // -bbarrett 2023-06-27

    setTimeout(() => {
      let initPayDate = undefined;

      if (this.activePayroll) {
        // Populate paydate from active payroll
        initPayDate = formatDatePickerValue(this.activePayroll.PayDate);
      } else if (this.selectedSchedules.length == 1) {
        // Populate paydate from single schedule
        initPayDate = formatDatePickerValue(this.selectedSchedules[0].ScheduledPayDate);
      } else if (this.selectedSchedules.length > 1) {
        // Populate paydate from nearest paydate of selected schedules
        initPayDate = formatDatePickerValue(
          minimumDate(this.selectedSchedules.map(s => s.ScheduledPayDate))
        );
      }
      // We do not initialize the paydate value for off-cycle payrolls
      if (initPayDate) {
        this.f.PayDate.setValue(initPayDate);
      }
    }, 1);

    this.payrollScheduleForm.addControl(
      'PayDate',
      new UntypedFormControl({ value: '', disabled: this.locked }, [Validators.required])
    );

    this.payrollScheduleForm.addControl('Schedules', this.createScheduleFormArray());

    if (this.payType != PayTypes.Regular) {
      this.payrollScheduleForm.addControl(
        'Label',
        new UntypedFormControl('', [Validators.required])
      );
    }

    if (this.authService.isInAnyRole(['CSR1', 'RunPayroll', 'Client Portal Admin'])) {
      let schedulePrefundDays: Observable<Date>[] = [];

      for (const schedule of this._selectedSchedules) {
        const payDateInfo = this.location.NextPayDates.find(
          x => x.CalendarId == schedule.CalendarId
        );
        schedulePrefundDays.push(this.getMinimumPrefundDate(payDateInfo));
      }

      forkJoin(schedulePrefundDays).subscribe(dates => {
        const largestPrefundDate = dates.reduce(function (a, b) {
          return a > b ? a : b;
        });
        const adjustedPrefundDate = GetTimeInEST(
          this.env.endOfDayCutoff.hours,
          this.env.endOfDayCutoff.minutes,
          largestPrefundDate
        );
        const now = new Date();
        if (!AreDatesEqual(now, adjustedPrefundDate)) {
          adjustedPrefundDate.setDate(adjustedPrefundDate.getDate() - 1);
        }
        this.minimumAvailableDate = formatDatePickerValue(adjustedPrefundDate);
      });
    }

    const latest = new Date();
    latest.setDate(latest.getDate() + 90);

    this.adminService
      .getHolidays(new Date(new Date().toDateString()), new Date(latest.toDateString()))
      .subscribe(holidays => {
        this.holidays = holidays;

        if (!this.activePayroll) {
          this.f.PayDate.setValidators([
            Validators.required,
            PayDateValidator(this.holidays).bind(this)
          ]);
        }

        this.f.PayDate.updateValueAndValidity({ emitEvent: false });
        if (this.f.Label) {
          this.f.Label.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  public getScheduleControls(index: number) {
    return (<UntypedFormGroup>(<UntypedFormGroup>this.f.Schedules).controls[index]).controls;
  }

  private createScheduleFormArray() {
    return this.formBuilder.array(
      this._selectedSchedules.map(s => {
        var group = this.formBuilder.group({
          periodEnd: [toNgbDate(s.PeriodEnd), Validators.required]
        });
        group.valueChanges.subscribe(val => {
          if (val.periodEnd) {
            s.PeriodEnd = fromNgbDate(val.periodEnd);
          }
        });
        return group;
      })
    );
  }

  private getMinimumPrefundDate(payDateInfo: PayDateInfoEx): Observable<Date> {
    if (payDateInfo.RequiredAchPrefundDays == 0 || payDateInfo.RequiredAchPrefundDays == null) {
      return of(new Date());
    } else {
      return this.businessDayService
        .getBusinessDay(new Date(), payDateInfo.RequiredAchPrefundDays)
        .pipe(map(date => new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)));
    }
  }

  public renderTodaysDate() {
    return new Date().toLocaleDateString();
  }

  public toggle;

  // #endregion
}
